import React, { FormEvent, useCallback, useContext, useEffect, useState } from "react";
import { ErrorCodes } from "manifest-core/api/errorCodes";
import WelcomeScreen from "./WelcomeScreen";
import { route, RoutePaths, useTypedNavigate } from "../../routes/RoutePaths";
import { AppContext } from "../../context/App/appProvider";

export type WelcomeControllerType = ReturnType<typeof createWelcomeController>;

const createWelcomeController = () => {
  const navigate = useTypedNavigate();
  const { login } = useContext(AppContext);

  const [unverifiedForgotPasswordError, setUnverifiedForgotPasswordError] = useState<"" | "login" | "forgotPassword">(
    ""
  );
  const [error, setError] = useState(false);
  const [passwordError, setPasswordError] = useState(error);
  const [errorMessage, setErrorMessage] = useState<JSX.Element | string>("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [allowLogin, setAllowLogin] = useState(false);
  const [highlightForm, setHighlightForm] = useState(false);

  const resetErrors = () => {
    setError(false);
    setUnverifiedForgotPasswordError("");
    setErrorMessage("");
    setPasswordError(false);
    setHighlightForm(false);
  };

  const validateLogin = useCallback(() => {
    if ((highlightForm || error) && email.length === 0) {
      setHighlightForm(false);
      setError(false);
    }
    if (passwordError && password.length === 0) {
      setPasswordError(false);
    }
    if (email.length > 0 && password.length > 0) {
      setAllowLogin(true);
    } else {
      setAllowLogin(false);
    }
  }, [email, password]);

  const submitLoginForm = async (formEvent: FormEvent) => {
    formEvent.preventDefault();
    resetErrors();
    try {
      const user = await login({
        email,
        password,
      });
      if (!user?.emailVerified) {
        return navigate(route(RoutePaths.accountVerifyEmail));
      }
      return navigate(route(RoutePaths.dashboard));
    } catch (e) {
      const thrownError = e as any;
      setError(true);
      if (thrownError.response?.data?.responseCode === ErrorCodes.E1006) {
        setUnverifiedForgotPasswordError("login");
      } else {
        setErrorMessage(
          <>
            The email and password you entered
            <br />
            don’t match. Please try again.
          </>
        );
      }
    }
  };

  const goToAccountCreationScreen = () => {
    navigate(route(RoutePaths.createAccount));
  };

  const handleForgotPassword = () => {
    navigate(route(RoutePaths.forgotPassword));
  };

  useEffect(() => {
    validateLogin();
  }, [email, passwordError, password]);

  return {
    goToAccountCreationScreen,
    allowLogin,
    email,
    error,
    errorMessage,
    handleForgotPassword,
    highlightForm,
    submitLoginForm,
    password,
    passwordError,
    setEmail,
    setPassword,
    unverifiedForgotPasswordError,
  };
};

export default function WelcomePage() {
  const controller = createWelcomeController();
  return <WelcomeScreen {...controller} />;
}
